import { Injectable, inputBinding } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, firstValueFrom } from 'rxjs';
import { UserDto } from '../../core/mceapi.client.generated';
import { AcknowledgeComponent } from './acknowledge/acknowledge.component';
import { ChangeEmailComponent } from './change-email/change-email.component';
import { ConfirmationComponent } from './confirmation/confirmation.component';
import { MoveCableDialogComponent } from './move-cable-dialog/move-cable-dialog.component';
import { InputDialogComponent, InputDialogType } from './input/input.component';
import { ShortCircuitKaComponent } from './short-circuit-ka/short-circuit-ka.component';
import { UnregisterUserComponent, UnregisterUserModel } from './unregister-user/unregister-user.component';
import { UserUpdateEmailDto } from '../../core/mceapi.client.generated';
import { UserEmailComponent, UserEmailModel } from './user-email/user-email.component';
import { CreditCostsComponent } from '../../account/credit-costs/credit-costs.component';
import { MeterEventsDialogComponent } from '../../account/meter-events/meter-events-dialog/meter-events-dialog.component';
import { ImpedanceZXRRatio, ImpedanceZXRRatioComponent } from './impedance-z-xr-ratio/impedance-z-xr-ratio.component';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(private dialog: MatDialog) { }

  confirm(title: string, message: string, height: string = '220px'): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      height,
      data: { title, message },
      disableClose: true
    });

    return dialogRef.afterClosed();
  }

  async confirmAsync(title: string, message: string, height: string = '220px'): Promise<boolean> {
    return firstValueFrom(this.confirm(title, message, height));
  }

  acknowledge(title: string, message: string, height: string = '220px'): Observable<boolean> {
    const dialogRef = this.dialog.open(AcknowledgeComponent, {
      height,
      data: { title, message },
      disableClose: true
    });

    return dialogRef.afterClosed();
  }

  moveCableProjectSelect(projectUid:string): Observable<string> {
    const dialogRef = this.dialog.open(MoveCableDialogComponent, {
      height: '420px', width: '520px',
      data: { toProjectUid: projectUid },
      disableClose: true
    });
    return dialogRef.afterClosed();
  }

  async moveCableProjectSelectAsync(projectUid: string): Promise<any> {
    return firstValueFrom(this.moveCableProjectSelect(projectUid));
  }

  async shortCircuitKa(type: { title: string, isDC: boolean }): Promise<any> {
    const dialogRef = this.dialog.open(ShortCircuitKaComponent, {
      height: '400px', width: '380px',
      data: { title: type.title, isDC: type.isDC },
      disableClose: true
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  async impedanceZXRRatio(type: { title: string }): Promise<ImpedanceZXRRatio | undefined> {
    const dialogRef = this.dialog.open(ImpedanceZXRRatioComponent, {
      height: '400px', width: '380px',
      data: { title: type.title },
      disableClose: true
    });
    return firstValueFrom(dialogRef.afterClosed());
  }


  input(type: InputDialogType, label:string, placeHolder:string, max?:number, min?:number, hintText?: string, initialValue?: string | number): Observable<string | number | undefined> {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      height: '380px', width: '420px',
      data: {
        type: type, label: label, placeHolder: placeHolder,
        max: max, min: min, hintText: hintText, initialValue: initialValue
      },
      disableClose: true
    });
    return dialogRef.afterClosed();
  }

  creditCosts(): Observable<void> {
    const dialogRef = this.dialog.open(CreditCostsComponent, {
      width: '520px',
      maxWidth: '95vw',
    });

    return dialogRef.afterClosed();
  }

  meterEvents(userUid: string): Observable<void> {
    const dialogRef = this.dialog.open(MeterEventsDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      bindings: [inputBinding('userUid', () => userUid)],
    });

    return dialogRef.afterClosed();
  }

  unregisterUser(): Promise<UnregisterUserModel> {
    const dialogRef = this.dialog.open(UnregisterUserComponent, {
      height: '480px', width: '520px',
      disableClose: true
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  changeEmail(): Promise<UserUpdateEmailDto> {
    const dialogRef = this.dialog.open(ChangeEmailComponent, {
      height: '560px', width: '520px',
      disableClose: true
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  userEmail(): Promise<UserEmailModel> {
    const dialogRef = this.dialog.open(UserEmailComponent, {
      height: '360px', width: '520px',
      disableClose: true
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

}
